/**
 * IngestFlow Gallery Page
 * 
 * Full-page asset gallery for browsing club content.
 * Club-agnostic - pulls from whichever Airtable base is configured via env vars.
 * 
 * Features:
 * - Grid view with thumbnails
 * - Search by filename
 * - Click to open detail sidebar
 * - Shows asset metadata (submitter, resolution, type, etc.)
 * - Preview and download links
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { fetchAllAssets, archiveAsset, restoreAsset } from "../airtable";
import commercialAirtable from "../services/commercialAirtable";
import { useAuth } from "../context/AuthContext";

// ============================================================
// STYLES
// ============================================================
const styles = {
  // Page container
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: "#f7f7f5",
    fontFamily: "'Space Grotesk', -apple-system, sans-serif",
  },
  
  // Dark header (matching uploader style)
  header: {
    backgroundColor: "#111114",
    padding: "14px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#fff",
    margin: 0,
  },
  headerSub: {
    fontSize: 13,
    color: "rgba(255,255,255,0.5)",
    marginTop: 2,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  
  // Search in header
  searchWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    color: "rgba(255,255,255,0.4)",
    pointerEvents: "none",
  },
  searchInput: {
    width: 280,
    padding: "10px 14px 10px 38px",
    fontSize: 14,
    color: "#fff",
    backgroundColor: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 8,
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color 0.2s, box-shadow 0.2s",
  },
  searchInputFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.15)",
  },
  
  // Asset count badge
  countBadge: {
    backgroundColor: "rgba(47, 214, 181, 0.15)",
    border: "1px solid rgba(47, 214, 181, 0.4)",
    color: "#2fd6b5",
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  },
  pdfExportBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: "#ef4444",
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s ease",
  },
  pdfExportBtnHover: {
    backgroundColor: "rgba(239, 68, 68, 0.2)",
    borderColor: "#ef4444",
  },
  
  // Category filter dropdown
  filterWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  filterSelect: {
    padding: "10px 36px 10px 14px",
    fontSize: 14,
    color: "#fff",
    backgroundColor: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 8,
    outline: "none",
    fontFamily: "inherit",
    cursor: "pointer",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.5)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    transition: "border-color 0.2s, box-shadow 0.2s",
    minWidth: 180,
  },
  filterSelectFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.15)",
  },
  clearFilterBtn: {
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 500,
    color: "#2fd6b5",
    backgroundColor: "rgba(47, 214, 181, 0.1)",
    border: "1px solid rgba(47, 214, 181, 0.3)",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  clearFilterBtnHover: {
    backgroundColor: "rgba(47, 214, 181, 0.2)",
    borderColor: "#2fd6b5",
  },
  
  // Teal accent line under header
  accentLine: {
    height: 3,
    background: "linear-gradient(90deg, #2fd6b5, #84cc16)",
  },
  
  // Content area
  content: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  gridContainer: {
    flex: 1,
    overflow: "auto",
    padding: 24,
    transition: "padding-right 0.2s ease",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 16,
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    cursor: "pointer",
    transition: "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
  },
  cardHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
    borderColor: "#2fd6b5",
  },
  cardSelected: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 2px rgba(47, 214, 181, 0.3)",
  },
  cardThumb: {
    width: "100%",
    height: 140,
    background: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  cardThumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  cardThumbPlaceholder: {
    fontSize: 40,
    color: "#d1d5db",
  },
  cardInfo: {
    padding: "12px 14px",
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: 4,
  },
  cardMeta: {
    fontSize: 12,
    color: "#6b7280",
  },
  cardType: {
    display: "inline-block",
    padding: "2px 6px",
    fontSize: 10,
    fontWeight: 500,
    background: "#f3f4f6",
    borderRadius: 4,
    color: "#6b7280",
    marginTop: 6,
  },
  // Sidebar styles
  sidebar: {
    width: 420,
    background: "#fff",
    borderLeft: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 100,
    boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.1)",
  },
  sidebarHidden: {
    display: "none",
  },
  sidebarHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sidebarTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  },
  closeBtn: {
    background: "none",
    border: "none",
    padding: 8,
    cursor: "pointer",
    color: "#6b7280",
    borderRadius: 6,
    fontSize: 18,
  },
  sidebarContent: {
    flex: 1,
    overflow: "auto",
    padding: 20,
  },
  previewContainer: {
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#f3f4f6",
    borderRadius: 8,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  previewImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  previewPlaceholder: {
    fontSize: 48,
    color: "#d1d5db",
  },
  assetTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    wordBreak: "break-all",
    marginBottom: 4,
  },
  assetDate: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 20,
  },
  metaSection: {
    marginBottom: 20,
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px 16px",
  },
  metaItem: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  metaLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  metaValue: {
    fontSize: 13,
    color: "#111827",
  },
  metaLink: {
    fontSize: 13,
    color: "#2fd6b5",
    textDecoration: "none",
  },
  specSection: {
    background: "#f9fafb",
    borderRadius: 8,
    padding: 14,
    marginTop: 16,
  },
  specTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 10,
  },
  specGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "6px 16px",
    fontSize: 12,
  },
  specLabel: {
    color: "#6b7280",
  },
  specValue: {
    color: "#111827",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#6b7280",
    fontSize: 14,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#6b7280",
    fontSize: 14,
    gap: 8,
  },
  emptyIcon: {
    fontSize: 48,
    opacity: 0.5,
  },
  
  // Load More button styles
  loadMoreContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "32px 0",
  },
  loadMoreBtn: {
    padding: "12px 32px",
    fontSize: 15,
    fontWeight: 600,
    color: "#000",
    background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "transform 0.15s, box-shadow 0.15s",
    boxShadow: "0 4px 12px rgba(47, 214, 181, 0.25)",
  },
  loadMoreBtnHover: {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(47, 214, 181, 0.35)",
  },
  
  // Preview Modal/Overlay styles
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.85)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  modalContent: {
    position: "relative",
    maxWidth: "90vw",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  modalClose: {
    position: "absolute",
    top: -40,
    right: -40,
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    color: "#fff",
    fontSize: 24,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
  },
  modalCloseHover: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  modalMedia: {
    maxWidth: "100%",
    maxHeight: "80vh",
    objectFit: "contain",
    borderRadius: 8,
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
  },
  modalTitle: {
    color: "#fff",
    fontSize: 14,
    fontWeight: 500,
    marginTop: 16,
    textAlign: "center",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  modalActions: {
    display: "flex",
    gap: 12,
    marginTop: 12,
  },
  modalActionBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: 6,
    cursor: "pointer",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: 6,
    transition: "background-color 0.2s",
  },
  modalActionBtnHover: {
    backgroundColor: "rgba(47, 214, 181, 0.3)",
    borderColor: "#2fd6b5",
  },
  modalPlaceholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: 400,
    height: 300,
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: 8,
    color: "rgba(255, 255, 255, 0.5)",
  },
  modalPlaceholderIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  modalPlaceholderText: {
    fontSize: 14,
  },
};

// ============================================================
// CONTENT CLASSIFICATION HELPERS
// ============================================================

/**
 * Parse content type from Asset Destination field
 * e.g. "MCG-tiled" → "LED Tiled", "Marvel-360" → "LED 360"
 */
function parseContentType(destination) {
  if (!destination) return "";
  const lower = destination.toLowerCase();
  if (lower.includes("tiled")) return "LED Tiled";
  if (lower.includes("360")) return "LED 360";
  if (lower.includes("interchange")) return "LED Interchange";
  if (lower.includes("iptv")) return "IPTV";
  if (lower.includes("tvc")) return "TVC";
  if (lower.includes("takeover")) return "Takeover";
  if (lower.includes("bigscreen") || lower.includes("big screen")) return "Big Screen";
  if (lower.includes("cornerscreen") || lower.includes("corner screen")) return "Corner Screen";
  if (lower.includes("superscreen") || lower.includes("super")) return "Superscreen";
  return "";
}

const CONTENT_TYPE_ICONS = {
  "LED Tiled": "🟩", "LED 360": "🔄", "LED Interchange": "🔁",
  "IPTV": "📺", "TVC": "🎬", "Takeover": "💡",
  "Big Screen": "📽️", "Corner Screen": "📐", "Superscreen": "🖥️",
};

/**
 * Parse venue from Asset Destination field
 */
function parseVenue(destination) {
  if (!destination) return "";
  const lower = destination.toLowerCase();
  if (lower.startsWith("mcg")) return "MCG";
  if (lower.startsWith("marvel") || lower.startsWith("mrv")) return "MRV";
  if (lower.startsWith("scg")) return "SCG";
  return "";
}

const VENUE_COLORS = { MCG: "#2dd4bf", MRV: "#818cf8", SCG: "#f59e0b" };

/**
 * Determine if content is Club or Commercial based on Asset Destination field.
 * If destination contains "-commercial", it's commercial content.
 */
function parseSourceType(destination) {
  if (!destination) return "";
  if (destination.toLowerCase().includes("-commercial")) return "Commercial";
  return "Club";
}

const SOURCE_CONFIG = {
  Club:       { color: "#d97706", bg: "#fef3c7", border: "#fcd34d" },
  Commercial: { color: "#2563eb", bg: "#dbeafe", border: "#93c5fd" },
};

// ============================================================
// HELPERS
// ============================================================

/**
 * Extract thumbnail URL from various Airtable formats
 */
function getThumbnailUrl(asset) {
  if (!asset) return null;
  
  const thumb = asset.thumb;
  if (!thumb) return null;
  
  // Array format (Airtable attachments)
  if (Array.isArray(thumb)) {
    const first = thumb[0];
    if (!first) return null;
    // Airtable attachment object
    if (first.thumbnails?.large?.url) return first.thumbnails.large.url;
    if (first.url) return first.url;
    if (typeof first === "string") return first;
  }
  
  // String format
  if (typeof thumb === "string" && thumb.trim()) {
    return thumb.split(/[,\n]/)[0].trim();
  }
  
  return null;
}

/**
 * Check if URL is a Dropbox URL
 */
function isDropboxUrl(url) {
  return url && (url.includes("dropbox.com") || url.includes("dl.dropboxusercontent.com"));
}

/**
 * Analyze Dropbox URL to determine if it's embeddable
 * Returns: { embeddable: boolean, type: 'image'|'video'|'mov'|'folder', embedUrl: string|null }
 */
function analyzeDropboxUrl(url) {
  if (!url || !isDropboxUrl(url)) {
    return { embeddable: false, type: null, embedUrl: null };
  }
  
  // Check for file extension in URL
  const hasExtension = /\.(jpg|jpeg|png|gif|mp4|mov|webm)(\?|$)/i.test(url);
  
  // Folder URLs (no extension, or /fo/ path)
  if (!hasExtension || url.includes("/fo/") || url.includes("/folder/")) {
    return { embeddable: false, type: "folder", embedUrl: null };
  }
  
  // MOV files - need Dropbox's player, can't embed directly
  if (/\.mov(\?|$)/i.test(url)) {
    return { embeddable: false, type: "mov", embedUrl: null };
  }
  
  // Images (jpg, png, gif) and MP4 - can embed with raw=1
  if (/\.(jpg|jpeg|png|gif)(\?|$)/i.test(url)) {
    const embedUrl = convertToDropboxRaw(url);
    return { embeddable: true, type: "image", embedUrl };
  }
  
  if (/\.mp4(\?|$)/i.test(url)) {
    const embedUrl = convertToDropboxRaw(url);
    return { embeddable: true, type: "video", embedUrl };
  }
  
  return { embeddable: false, type: "unknown", embedUrl: null };
}

/**
 * Convert Dropbox share URL to raw/embeddable URL
 */
function convertToDropboxRaw(url) {
  if (!url) return null;
  
  // Already a raw URL
  if (url.includes("dl.dropboxusercontent.com")) {
    return url;
  }
  
  // Convert www.dropbox.com to dl.dropboxusercontent.com
  // Or just change dl=0 to raw=1
  let embedUrl = url;
  
  // Replace domain for direct access
  embedUrl = embedUrl.replace("www.dropbox.com", "dl.dropboxusercontent.com");
  
  // Handle query params
  if (embedUrl.includes("?")) {
    // Remove dl=0 and add raw=1
    embedUrl = embedUrl.replace(/[?&]dl=\d/, "");
    embedUrl = embedUrl.replace(/[?&]raw=\d/, "");
    embedUrl += (embedUrl.includes("?") ? "&" : "?") + "raw=1";
  } else {
    embedUrl += "?raw=1";
  }
  
  return embedUrl;
}

/**
 * Get preview URL (for larger view)
 */
function getPreviewUrl(asset) {
  if (!asset) return null;
  
  // Try previewUrl first
  if (asset.previewUrl) {
    if (typeof asset.previewUrl === "string") {
      return asset.previewUrl.split(/[,\n]/)[0].trim();
    }
    if (Array.isArray(asset.previewUrl) && asset.previewUrl[0]) {
      const first = asset.previewUrl[0];
      return first.url || first;
    }
  }
  
  // Fall back to thumb
  return getThumbnailUrl(asset);
}

/**
 * Format date for display
 */
function formatDate(dateString) {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

/**
 * Format duration from seconds to mm:ss.ms
 */
function formatDuration(seconds) {
  if (typeof seconds !== "number" || isNaN(seconds)) return null;
  
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.round((seconds % 1) * 1000);
  
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
  }
  return `${secs}.${String(ms).padStart(3, "0")}s`;
}

/**
 * Parse Asset Spec Summary into key-value pairs
 * Handles empty values like "alpha:  audio: true"
 */
function parseSpecSummary(summary) {
  if (!summary) return [];
  
  const pairs = [];
  
  // Split by known field names to handle empty values properly
  // This regex finds "fieldname:" patterns
  const fieldPattern = /\b(file type|resolution|frame rate|duration|format|alpha|audio|channels|sample rate|type)\s*:/gi;
  
  // Find all field positions
  const fields = [];
  let fieldMatch;
  while ((fieldMatch = fieldPattern.exec(summary)) !== null) {
    fields.push({
      key: fieldMatch[1],
      start: fieldMatch.index,
      valueStart: fieldMatch.index + fieldMatch[0].length
    });
  }
  
  // Extract values based on positions
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    const nextField = fields[i + 1];
    
    // Value is everything between this field's colon and the next field (or end of string)
    const valueEnd = nextField ? nextField.start : summary.length;
    let value = summary.slice(field.valueStart, valueEnd).trim();
    
    // Clean up any trailing field names that might have been captured
    value = value.replace(/\s+\w[\w\s]*?:\s*$/, "").trim();
    
    // Use em-dash for empty values
    if (!value) value = "—";
    
    let key = field.key.toLowerCase();
    
    // Format duration values
    if (key === "duration" && /^\d+(\.\d+)?$/.test(value)) {
      value = formatDuration(parseFloat(value));
    }
    
    // Format sample rate with Hz
    if (key === "sample rate" && /^\d+$/.test(value)) {
      const rate = parseInt(value, 10);
      value = rate >= 1000 ? `${(rate / 1000).toFixed(0)} kHz` : `${rate} Hz`;
    }
    
    pairs.push({ key, value });
  }
  
  return pairs;
}

/**
 * Spec Summary Table Component - VenueFlow styled
 */
function SpecSummaryTable({ summary }) {
  const specs = parseSpecSummary(summary);
  
  if (specs.length === 0) {
    return (
      <div style={{ fontSize: 13, color: "#6b7280" }}>
        {summary}
      </div>
    );
  }
  
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "auto 1fr",
      gap: "8px 16px",
      fontSize: 13,
    }}>
      {specs.map(({ key, value }, index) => (
        <React.Fragment key={index}>
          <span style={{
            color: "#6b7280",
            fontWeight: 500,
            textTransform: "capitalize",
          }}>
            {key}
          </span>
          <span style={{
            color: value === "—" ? "#9ca3af" : "#111827",
            fontFamily: /^\d|various/i.test(value) ? "'SF Mono', 'Consolas', monospace" : "inherit",
          }}>
            {value}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * Detect file type from filename
 */
function getFileType(title) {
  if (!title) return "unknown";
  
  const ext = title.split(".").pop()?.toLowerCase() || "";
  
  if (["mp4", "mov", "webm", "m4v", "avi"].includes(ext)) return "video";
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) return "image";
  if (["pdf"].includes(ext)) return "pdf";
  
  return "file";
}

/**
 * Parse asset spec info from title/destination
 */
function parseAssetSpecs(asset) {
  // These would ideally come from Airtable fields
  // For now, we extract what we can from the asset
  return {
    type: getFileType(asset.title),
    resolution: asset.resolution || null,
    frameRate: asset.frameRate || null,
    duration: asset.duration || null,
    format: asset.format || null,
    alpha: asset.hasAlpha ? "true" : "false",
    audio: asset.hasAudio ? "true" : "false",
    channels: asset.channels || null,
    sampleRate: asset.sampleRate || null,
  };
}

// ============================================================
// PDF EXPORT - Content Library Report
// ============================================================
function generateContentListPDF(assets, filterLabel) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
  const timeStr = now.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  const clubCode = process.env.REACT_APP_DEFAULT_CLUB || "STK";

  // Header bar
  doc.setFillColor(17, 17, 20);
  doc.rect(0, 0, pageWidth, 18, "F");
  doc.setFillColor(47, 214, 181);
  doc.rect(0, 18, pageWidth, 1.2, "F");

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text("MatchFlow Library — Content Report", 14, 11);

  // Subtitle
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text(`${filterLabel}  ·  ${assets.length} asset${assets.length !== 1 ? "s" : ""}  ·  Generated ${dateStr} ${timeStr}`, 14, 16);

  // Club badge right
  doc.setFontSize(9);
  doc.setTextColor(47, 214, 181);
  doc.text(`${clubCode}  ·  Lumen8 VenueFlow`, pageWidth - 14, 11, { align: "right" });

  // Build table data
  const tableData = assets.map((asset, idx) => {
    const fileType = getFileType(asset.title);
    const ext = (asset.title || "").split(".").pop()?.toUpperCase() || "—";
    const destination = asset.destination || "—";
    const uploaded = asset.createdTime
      ? new Date(asset.createdTime).toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "—";
    const uploadedTime = asset.createdTime
      ? new Date(asset.createdTime).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })
      : "";
    const uploader = asset.submitterEmail
      ? (() => {
          const local = asset.submitterEmail.split("@")[0];
          const parts = local.split(/[._-]/);
          if (parts.length >= 2) {
            return parts[0].charAt(0).toUpperCase() + parts[0].slice(1) + " " + parts[1].charAt(0).toUpperCase() + ".";
          }
          return local;
        })()
      : "—";

    return [idx + 1, asset.title || "Untitled", fileType.toUpperCase(), ext, destination, uploader, uploaded, uploadedTime];
  });

  // Table
  doc.autoTable({
    startY: 22,
    head: [["#", "Content Name", "Type", "Format", "Destination", "Uploaded By", "Date", "Time"]],
    body: tableData,
    theme: "grid",
    headStyles: {
      fillColor: [30, 30, 42],
      textColor: [148, 163, 184],
      fontStyle: "bold",
      fontSize: 7.5,
      cellPadding: 3,
      lineColor: [50, 50, 65],
      lineWidth: 0.2,
    },
    bodyStyles: {
      fontSize: 7.5,
      cellPadding: 2.5,
      textColor: [30, 30, 50],
      lineColor: [220, 225, 230],
      lineWidth: 0.15,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: {
      0: { cellWidth: 10, halign: "center", textColor: [148, 163, 184] },
      1: { cellWidth: "auto" },
      2: { cellWidth: 18, halign: "center" },
      3: { cellWidth: 16, halign: "center" },
      4: { cellWidth: 50 },
      5: { cellWidth: 30 },
      6: { cellWidth: 22, halign: "center" },
      7: { cellWidth: 16, halign: "center" },
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      // Footer on every page
      const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
      const totalPages = doc.internal.getNumberOfPages();
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Lumen8 VenueFlow  ·  MatchFlow Library  ·  ${filterLabel}  ·  ${dateStr}`,
        14,
        pageHeight - 6
      );
      doc.text(
        `Page ${pageNum} of ${totalPages}  ·  Confidential`,
        pageWidth - 14,
        pageHeight - 6,
        { align: "right" }
      );
    },
  });

  // Save
  const filename = `VenueFlow_Content_Report_${clubCode}_${now.toISOString().slice(0, 10).replace(/-/g, "")}.pdf`;
  doc.save(filename);
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const ASSETS_PER_PAGE = 48;

// Category options for filter dropdown
// IMPORTANT: Match terms should be specific to destination field patterns to avoid overlap
// Destination values are like: "MCG-bigscreen", "Marvel-360", "superscreen-16:9", etc.
const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "big-screen", label: "Big Screen", match: ["-bigscreen", "big screen - gfx", "big screen - videos"] },
  { value: "corner-screens", label: "Corner Screens", match: ["-cornerscreen", "corner screen", "corner screens - gfx", "corner screens - videos"] },
  { value: "superscreen", label: "Superscreen", match: ["superscreen-", "-super-", "-superfull", "-superside", "-super169"] },
  { value: "led", label: "LED Ribbons", match: ["-tiled", "-360", "-interchange", "-overlay", "-ponsford", "-ribbon"] },
  { value: "iptv", label: "IPTV", match: ["-iptv"] },
  { value: "tvc", label: "TVC", match: ["-tvc", "screen - tvc"] },
];

// Specific producer domains that should only see their own uploads in IngestFlow Gallery
const RESTRICTED_PRODUCER_DOMAINS = ['mktg.com', 'kojo.co'];

// ✅ Available rounds for TVC round allocation editing
const AVAILABLE_ROUNDS = ["OR", "R1", "R2", "R3", "R4", "R5", "R6", "R7", "R8", "R9", "R10", "R11", "R12", "R13", "R14", "R15", "R16", "R17", "R18", "R19", "R20", "R21", "R22", "R23", "R24"];

// ✅ Parse Round Allocations JSON string into array
function parseRoundAllocations(jsonStr) {
  if (!jsonStr) return [];
  try {
    const parsed = JSON.parse(jsonStr);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function IngestFlowGalleryPage() {
  const { userEmail } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(""); // Category filter
  const [sourceFilter, setSourceFilter] = useState(""); // Club / Commercial filter
  const [venueFilter, setVenueFilter] = useState(""); // Venue filter (MCG / MRV / SCG)
  const [sortOrder, setSortOrder] = useState("newest"); // newest / oldest
  const [filterFocused, setFilterFocused] = useState(false);
  const [sourceFocused, setSourceFocused] = useState(false);
  const [clearFilterHovered, setClearFilterHovered] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [previewModalAsset, setPreviewModalAsset] = useState(null); // For fullscreen preview modal
  const [visibleCount, setVisibleCount] = useState(ASSETS_PER_PAGE); // Pagination
  const [pdfBtnHovered, setPdfBtnHovered] = useState(false);
  const [showArchived, setShowArchived] = useState(false); // Toggle to show/hide archived assets
  const [archiving, setArchiving] = useState(null); // Track which asset is being archived
  const [clubHomeRounds, setClubHomeRounds] = useState(null); // Dynamic rounds from fixtures (null = loading)

  // Get user's email domain for filtering (e.g., "mktg.com" from "user@mktg.com")
  const userDomain = useMemo(() => {
    if (!userEmail) return null;
    const parts = userEmail.toLowerCase().split('@');
    return parts.length === 2 ? parts[1] : null;
  }, [userEmail]);

  // Only apply domain filtering for MKTG and Kojo users
  const restrictedDomain = useMemo(() => {
    if (!userDomain) return null;
    return RESTRICTED_PRODUCER_DOMAINS.includes(userDomain) ? userDomain : null;
  }, [userDomain]);

  // Fetch assets on mount
  useEffect(() => {
    async function loadAssets() {
      try {
        setLoading(true);
        setError(null);
        
        // ✅ PERF: Fire all three independent fetches in parallel
        // Previously these ran sequentially: assets → roundAllocations → homeRounds
        // Now they overlap, cutting total load time significantly
        const clubCode = (process.env.REACT_APP_DEFAULT_CLUB || "").toUpperCase();
        const allClubs = { ...require("../config/clubBases").AFL_CLUBS, ...require("../config/clubBases").NRL_CLUBS };
        const clubName = allClubs[clubCode]?.name || "";

        const [assetsMap, raMap, homeRounds] = await Promise.all([
          fetchAllAssets(),
          commercialAirtable.fetchRoundAllocationsMap().catch(err => {
            console.warn("[Gallery] Could not fetch round allocations:", err.message);
            return {};
          }),
          clubName
            ? commercialAirtable.getClubHomeRounds(clubName).catch(err => {
                console.warn("[Gallery] Could not fetch club home rounds:", err.message);
                return [];
              })
            : Promise.resolve([]),
        ]);

        // Convert Map to array and sort by createdTime (newest first)
        let assetsArray = Array.from(assetsMap.values())
          .sort((a, b) => {
            const dateA = new Date(a.createdTime || 0);
            const dateB = new Date(b.createdTime || 0);
            return dateB - dateA;
          });

        // Cross-reference with commercial content master base for Round Allocations
        if (raMap && Object.keys(raMap).length > 0) {
          assetsArray = assetsArray.map((asset) => {
            const lumenId = (asset.lumenId || "").trim();
            const match = lumenId ? raMap[lumenId] : null;
            if (match) {
              return {
                ...asset,
                roundAllocations: match.roundAllocations,
                contentRecordId: match.contentRecordId,
              };
            }
            return asset;
          });
          console.log("[Gallery] Merged round allocations from commercial base for", Object.keys(raMap).length, "records");
        }
        
        // Set club home rounds
        if (homeRounds && homeRounds.length > 0) {
          setClubHomeRounds(homeRounds);
        }
        
        setAssets(assetsArray);
      } catch (err) {
        console.error("Failed to load assets:", err);
        setError(err.message || "Failed to load assets");
      } finally {
        setLoading(false);
      }
    }
    
    loadAssets();
  }, []);

  // Reset visible count when search or filter changes
  useEffect(() => {
    setVisibleCount(ASSETS_PER_PAGE);
  }, [searchTerm, categoryFilter, sourceFilter, venueFilter, sortOrder]);

  // Filter assets by search term, category, and archived status
  const filteredAssets = useMemo(() => {
    let result = assets;
    
    // ✅ Restricted domain filter: MKTG and Kojo users only see their company's uploads
    // Everyone else (club users, Lumen8 staff, other producers) sees everything
    if (restrictedDomain) {
      result = result.filter((asset) => {
        const submitterEmail = (asset.submitterEmail || "").toLowerCase();
        return submitterEmail.endsWith(`@${restrictedDomain}`);
      });
    }
    
    // Filter out archived assets unless showArchived is true
    if (!showArchived) {
      result = result.filter((asset) => !asset.archived);
    }
    
    // Apply category filter (only checks destination field, not title)
    if (categoryFilter) {
      const categoryOption = CATEGORY_OPTIONS.find(opt => opt.value === categoryFilter);
      if (categoryOption?.match) {
        result = result.filter((asset) => {
          const destination = (asset.destination || "").toLowerCase();
          // Only match on destination to avoid false positives from title matches
          return categoryOption.match.some(term => 
            destination.includes(term)
          );
        });
      }
    }
    
    // Apply source type filter (Club / Commercial)
    if (sourceFilter) {
      result = result.filter((asset) => {
        return parseSourceType(asset.destination) === sourceFilter;
      });
    }
    
    // Apply search filter (searches title, destination, and Lumen8 ID)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((asset) => {
        const title = (asset.title || "").toLowerCase();
        const destination = (asset.destination || "").toLowerCase();
        const lumenId = (asset.lumenId || "").toLowerCase();
        return title.includes(term) || destination.includes(term) || lumenId.includes(term);
      });
    }
    
    // Apply venue filter
    if (venueFilter) {
      result = result.filter((asset) => parseVenue(asset.destination) === venueFilter);
    }
    
    // Apply sort order
    if (sortOrder === "oldest") {
      result = [...result].sort((a, b) => new Date(a.createdTime || 0) - new Date(b.createdTime || 0));
    }
    // Default (newest) is already sorted from fetch
    
    return result;
  }, [assets, searchTerm, categoryFilter, sourceFilter, venueFilter, sortOrder, showArchived, restrictedDomain]);

  // Count archived assets for badge
  const archivedCount = useMemo(() => {
    return assets.filter(a => a.archived).length;
  }, [assets]);

  // Handle archive/restore
  const handleArchive = useCallback(async (assetId, lumen8Id) => {
    setArchiving(assetId);
    try {
      await archiveAsset(assetId, lumen8Id);
      // Update local state
      setAssets(prev => prev.map(a => 
        a.id === assetId ? { ...a, archived: true } : a
      ));
      // Update selectedAsset if it's the one being archived
      setSelectedAsset(prev => 
        prev?.id === assetId ? { ...prev, archived: true } : prev
      );
    } catch (err) {
      console.error("Failed to archive asset:", err);
      alert(err.message || "Failed to archive asset");
    } finally {
      setArchiving(null);
    }
  }, []);

  const handleRestore = useCallback(async (assetId, lumen8Id) => {
    setArchiving(assetId);
    try {
      await restoreAsset(assetId, lumen8Id);
      // Update local state
      setAssets(prev => prev.map(a => 
        a.id === assetId ? { ...a, archived: false } : a
      ));
      // Update selectedAsset if it's the one being restored
      setSelectedAsset(prev => 
        prev?.id === assetId ? { ...prev, archived: false } : prev
      );
    } catch (err) {
      console.error("Failed to restore asset:", err);
      alert(err.message || "Failed to restore asset");
    } finally {
      setArchiving(null);
    }
  }, []);

  // Handle round allocations update — writes to commercial content master base
  const handleUpdateRounds = useCallback(async (assetId, newAllocations) => {
    try {
      // Find the asset to get its contentRecordId (commercial content master base record)
      const asset = assets.find((a) => a.id === assetId);
      let contentRecordId = asset?.contentRecordId;

      // Fallback: look up by Lumen8 ID if contentRecordId not yet populated
      // (happens when Round Allocations haven't been set before — fetchRoundAllocationsMap only returns records that already have them)
      if (!contentRecordId) {
        const lookupId = asset?.lumenId || asset?.name || asset?.fileName || "";
        if (lookupId) {
          console.log(`[Gallery] contentRecordId missing, looking up by Lumen8 ID: "${lookupId}"`);
          contentRecordId = await commercialAirtable.lookupContentRecordByLumenId(lookupId);
        }
        if (!contentRecordId) {
          throw new Error("Could not find this asset in the Content Library. It may not have been processed yet — try again shortly.");
        }
        // Cache it on the asset so future edits don't need another lookup
        setAssets(prev => prev.map(a =>
          a.id === assetId ? { ...a, contentRecordId } : a
        ));
      }

      await commercialAirtable.updateContentRoundAllocations(contentRecordId, newAllocations);
      const newJson = JSON.stringify(newAllocations);

      // Update local state
      setAssets(prev => prev.map(a =>
        a.id === assetId ? { ...a, roundAllocations: newJson, contentRecordId } : a
      ));
      // Update selectedAsset
      setSelectedAsset(prev =>
        prev?.id === assetId ? { ...prev, roundAllocations: newJson, contentRecordId } : prev
      );
    } catch (err) {
      console.error("Failed to update round allocations:", err);
      alert(err.message || "Failed to update round allocations");
    }
  }, [assets]);

  // PDF export handler
  const handleExportPDF = useCallback(() => {
    if (filteredAssets.length === 0) return;
    const filterLabel = categoryFilter
      ? CATEGORY_OPTIONS.find((opt) => opt.value === categoryFilter)?.label || "All Content"
      : "All Content";
    generateContentListPDF(filteredAssets, filterLabel);
  }, [filteredAssets, categoryFilter]);

  // Paginated assets to display
  const visibleAssets = useMemo(() => {
    return filteredAssets.slice(0, visibleCount);
  }, [filteredAssets, visibleCount]);

  // Check if there are more to load
  const hasMore = visibleCount < filteredAssets.length;

  // Load more handler
  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + ASSETS_PER_PAGE);
  }, []);

  // Handle card click
  const handleCardClick = useCallback((asset) => {
    setSelectedAsset(asset);
  }, []);

  // Close sidebar
  const closeSidebar = useCallback(() => {
    setSelectedAsset(null);
  }, []);

  // Get dynamic header text based on category filter
  const getHeaderTitle = () => {
    if (!categoryFilter) return "All Content";
    const option = CATEGORY_OPTIONS.find(opt => opt.value === categoryFilter);
    return option?.label || "All Content";
  };

  // Render loading state
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          Loading assets...
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyState}>
          <span style={styles.emptyIcon}>⚠️</span>
          <span>Error: {error}</span>
        </div>
      </div>
    );
  }

  const headerTitle = getHeaderTitle();

  return (
    <div style={styles.page}>
      {/* Dark Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.headerTitle}>{headerTitle}</h1>
          <p style={styles.headerSub}>Browse and preview uploaded content</p>
        </div>
        <div style={styles.headerRight}>
          {/* Category Filter */}
          <div style={styles.filterWrapper}>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              onFocus={() => setFilterFocused(true)}
              onBlur={() => setFilterFocused(false)}
              style={{
                ...styles.filterSelect,
                ...(filterFocused ? styles.filterSelectFocus : {}),
              }}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} style={{ backgroundColor: "#1a1a24", color: "#fff" }}>
                  {opt.label}
                </option>
              ))}
            </select>
            
            {/* Source Type Filter (Club / Commercial) */}
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              onFocus={() => setSourceFocused(true)}
              onBlur={() => setSourceFocused(false)}
              style={{
                ...styles.filterSelect,
                minWidth: 160,
                ...(sourceFocused ? styles.filterSelectFocus : {}),
              }}
            >
              <option value="" style={{ backgroundColor: "#1a1a24", color: "#fff" }}>Club / Commercial</option>
              <option value="Club" style={{ backgroundColor: "#1a1a24", color: "#fff" }}>Club</option>
              <option value="Commercial" style={{ backgroundColor: "#1a1a24", color: "#fff" }}>Commercial</option>
            </select>
            
            {/* Venue Filter */}
            <select
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
              style={{
                ...styles.filterSelect,
                minWidth: 130,
              }}
            >
              <option value="" style={{ backgroundColor: "#1a1a24", color: "#fff" }}>All Venues</option>
              <option value="MCG" style={{ backgroundColor: "#1a1a24", color: "#fff" }}>MCG</option>
              <option value="MRV" style={{ backgroundColor: "#1a1a24", color: "#fff" }}>Marvel Stadium</option>
              <option value="SCG" style={{ backgroundColor: "#1a1a24", color: "#fff" }}>SCG</option>
            </select>
            
            {/* Sort Order Toggle */}
            <button
              onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
              title={sortOrder === "newest" ? "Showing newest first — click for oldest first" : "Showing oldest first — click for newest first"}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 14px", fontSize: 12, fontWeight: 600,
                color: "#e2e8f0", backgroundColor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
                cursor: "pointer", whiteSpace: "nowrap",
                transition: "all 0.15s ease",
              }}
            >
              {sortOrder === "newest" ? "↓ Newest" : "↑ Oldest"}
            </button>
            
            {/* Clear filter button - only show when any filter is active */}
            {(categoryFilter || sourceFilter || venueFilter) && (
              <button
                onClick={() => { setCategoryFilter(""); setSourceFilter(""); setVenueFilter(""); }}
                onMouseEnter={() => setClearFilterHovered(true)}
                onMouseLeave={() => setClearFilterHovered(false)}
                style={{
                  ...styles.clearFilterBtn,
                  ...(clearFilterHovered ? styles.clearFilterBtnHover : {}),
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Clear
              </button>
            )}
          </div>
          
          {/* Search */}
          <div style={styles.searchWrapper}>
            <svg style={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, Lumen8 ID, or destination..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              style={{
                ...styles.searchInput,
                ...(searchFocused ? styles.searchInputFocus : {}),
              }}
            />
          </div>
          {/* Asset count badge */}
          <span style={styles.countBadge}>
            {filteredAssets.length} asset{filteredAssets.length !== 1 ? "s" : ""}
          </span>
          
          {/* PDF Export Button */}
          {filteredAssets.length > 0 && (
            <button
              onClick={handleExportPDF}
              onMouseEnter={() => setPdfBtnHovered(true)}
              onMouseLeave={() => setPdfBtnHovered(false)}
              title="Download content list as PDF"
              style={{
                ...styles.pdfExportBtn,
                ...(pdfBtnHovered ? styles.pdfExportBtnHover : {}),
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <polyline points="9 15 12 18 15 15" />
              </svg>
              PDF
            </button>
          )}
          
          {/* Show Archived Toggle */}
          <button
            onClick={() => setShowArchived(!showArchived)}
            title={showArchived ? "Hide archived assets" : "Show archived assets"}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 600,
              color: showArchived ? "#f59e0b" : "rgba(255,255,255,0.5)",
              backgroundColor: showArchived ? "rgba(245, 158, 11, 0.15)" : "rgba(255,255,255,0.08)",
              border: `1px solid ${showArchived ? "rgba(245, 158, 11, 0.4)" : "rgba(255,255,255,0.15)"}`,
              borderRadius: 6,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "all 0.15s ease",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="21 8 21 21 3 21 3 8" />
              <rect x="1" y="3" width="22" height="5" />
              <line x1="10" y1="12" x2="14" y2="12" />
            </svg>
            {showArchived ? `Archived (${archivedCount})` : "Show Archived"}
          </button>
        </div>
      </header>
      
      {/* Teal accent line */}
      <div style={styles.accentLine} />

      {/* Content */}
      <div style={styles.content}>
        {/* Grid */}
        <div style={{
          ...styles.gridContainer,
          paddingRight: selectedAsset ? 444 : 24, // Make room for fixed sidebar when open
        }}>
          {filteredAssets.length === 0 ? (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>📁</span>
              <span>
                {searchTerm || venueFilter || categoryFilter ? "No assets match your filters" : "No assets found"}
              </span>
            </div>
          ) : (
            <>
              <div style={styles.grid}>
                {visibleAssets.map((asset) => {
                  const isSelected = selectedAsset?.id === asset.id;
                  const isHovered = hoveredCard === asset.id;
                  const thumbnail = getThumbnailUrl(asset);
                  // Use Asset Type from Airtable, fallback to filename detection
                  const fileType = asset.assetType || getFileType(asset.title);
                  // Derive venue, content type, and source from asset fields
                  const venue = parseVenue(asset.destination);
                  const contentType = parseContentType(asset.destination);
                  const sourceType = parseSourceType(asset.destination);
                  const venueColor = VENUE_COLORS[venue];
                  const sourceConf = SOURCE_CONFIG[sourceType];
                  
                  return (
                    <div
                      key={asset.id}
                      onClick={() => handleCardClick(asset)}
                      onMouseEnter={() => setHoveredCard(asset.id)}
                      onMouseLeave={() => setHoveredCard(null)}
                      style={{
                        ...styles.card,
                        ...(isHovered ? styles.cardHover : {}),
                        ...(isSelected ? styles.cardSelected : {}),
                        ...(asset.archived ? { opacity: 0.6 } : {}),
                      }}
                    >
                      <div style={{ ...styles.cardThumb, position: 'relative' }}>
                        {thumbnail ? (
                          <img
                            src={thumbnail}
                            alt=""
                            style={styles.cardThumbImg}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <span style={styles.cardThumbPlaceholder}>
                            {fileType === "video" ? "🎬" : "🖼️"}
                          </span>
                        )}
                        {/* Archived badge */}
                        {asset.archived && (
                          <div style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            padding: '3px 8px',
                            background: 'rgba(245, 158, 11, 0.9)',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#000',
                          }}>
                            ARCHIVED
                          </div>
                        )}
                        {/* Archive/Restore button on hover */}
                        {venue && (
                          <div style={{
                            position: 'absolute',
                            bottom: 6,
                            left: 6,
                            padding: '2px 6px',
                            background: 'rgba(0,0,0,0.7)',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            color: venueColor || '#fff',
                          }}>
                            {venue}
                          </div>
                        )}
                        {isHovered && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              asset.archived ? handleRestore(asset.id, asset.lumenId) : handleArchive(asset.id, asset.lumenId);
                            }}
                            disabled={archiving === asset.id}
                            title={asset.archived ? "Restore asset" : "Archive asset"}
                            style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              padding: '6px 10px',
                              background: asset.archived ? 'rgba(34, 197, 94, 0.9)' : 'rgba(0,0,0,0.7)',
                              border: 'none',
                              borderRadius: 6,
                              cursor: archiving === asset.id ? 'wait' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#fff',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {archiving === asset.id ? (
                              '...'
                            ) : asset.archived ? (
                              <>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="1 4 1 10 7 10" />
                                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                                </svg>
                                Restore
                              </>
                            ) : (
                              <>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="21 8 21 21 3 21 3 8" />
                                  <rect x="1" y="3" width="22" height="5" />
                                </svg>
                                Archive
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      <div style={styles.cardInfo}>
                        <div style={styles.cardTitle} title={asset.title}>
                          {asset.title || "Untitled"}
                        </div>
                        <div style={styles.cardMeta}>
                          {formatDate(asset.createdTime)}
                        </div>
                        {/* Tags row: Source + Content Type + Venue + File Type */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                          {sourceConf && (
                            <span style={{
                              display: "inline-block",
                              padding: "2px 6px",
                              fontSize: 10,
                              fontWeight: 600,
                              borderRadius: 4,
                              color: sourceConf.color,
                              background: sourceConf.bg,
                              border: `1px solid ${sourceConf.border}`,
                            }}>
                              {sourceType}
                            </span>
                          )}
                          {contentType && (
                            <span style={{
                              display: "inline-block",
                              padding: "2px 6px",
                              fontSize: 10,
                              fontWeight: 500,
                              borderRadius: 4,
                              color: "#6b7280",
                              background: "#f3f4f6",
                            }}>
                              {CONTENT_TYPE_ICONS[contentType] || "📄"} {contentType}
                            </span>
                          )}
                          {venue && (
                            <span style={{
                              display: "inline-block",
                              padding: "2px 6px",
                              fontSize: 10,
                              fontWeight: 600,
                              borderRadius: 4,
                              color: venueColor || "#6b7280",
                              background: venueColor ? `${venueColor}18` : "#f3f4f6",
                            }}>
                              {venue}
                            </span>
                          )}
                          <span style={styles.cardType}>{fileType}</span>
                        </div>
                        {/* Round allocation pills */}
                        {(() => {
                          const rounds = parseRoundAllocations(asset.roundAllocations);
                          if (!rounds.length) return null;
                          return (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>
                              {rounds.map((alloc, i) => (
                                <span key={i} style={{
                                  fontSize: 9,
                                  fontWeight: 600,
                                  padding: "1px 5px",
                                  borderRadius: 3,
                                  background: "rgba(47, 214, 181, 0.15)",
                                  color: "#0d9488",
                                  lineHeight: "16px",
                                }}>
                                  {alloc.round || alloc.Round || "?"}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Load More Button */}
              {hasMore && (
                <div style={styles.loadMoreContainer}>
                  <button
                    onClick={loadMore}
                    style={styles.loadMoreBtn}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 6px 16px rgba(47, 214, 181, 0.35)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "none";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(47, 214, 181, 0.25)";
                    }}
                  >
                    Load More ({filteredAssets.length - visibleCount} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <div 
          style={{
            ...styles.sidebar,
            ...(selectedAsset ? {} : styles.sidebarHidden),
          }}
        >
          {selectedAsset && (
            <AssetDetailSidebar
              asset={selectedAsset}
              onClose={closeSidebar}
              onOpenPreview={() => setPreviewModalAsset(selectedAsset)}
              onArchive={handleArchive}
              onRestore={handleRestore}
              onUpdateRounds={handleUpdateRounds}
              archiving={archiving}
              clubHomeRounds={clubHomeRounds}
            />
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewModalAsset && (
        <PreviewModal
          asset={previewModalAsset}
          onClose={() => setPreviewModalAsset(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// ASSET DETAIL SIDEBAR
// ============================================================
function AssetDetailSidebar({ asset, onClose, onOpenPreview, onArchive, onRestore, onUpdateRounds, archiving, clubHomeRounds }) {
  const { user } = useAuth();
  const [showRoundPicker, setShowRoundPicker] = useState(false);
  const [roundSaving, setRoundSaving] = useState(false);
  const [pendingRound, setPendingRound] = useState(null); // { round: "R3" } — awaiting plays selection
  const [pendingPlays, setPendingPlays] = useState(null); // awaiting paid/bonus selection
  const thumbnailUrl = getThumbnailUrl(asset);
  const previewUrl = getPreviewUrl(asset);
  const specs = parseAssetSpecs(asset);
  const isVideo = specs.type === "video";
  
  // Check if current user is Lumen8 staff
  const isLumen8Staff = user?.email?.toLowerCase().endsWith('@lumen8.com.au');
  
  // Debug logging
  console.log('Contact Uploader Debug:', {
    userEmail: user?.email,
    isLumen8Staff,
    submitterEmail: asset.submitterEmail,
    showButton: isLumen8Staff && asset.submitterEmail
  });
  
  // Analyze Dropbox URL for embedding capability
  const dropboxInfo = analyzeDropboxUrl(previewUrl);
  
  // For display in sidebar, prefer thumbnail, then embeddable Dropbox, then other URLs
  const displayUrl = thumbnailUrl || 
    (dropboxInfo.embeddable ? dropboxInfo.embedUrl : null) || 
    (!isDropboxUrl(previewUrl) ? previewUrl : null);
  
  // Determine if we can open in modal vs new tab
  const canOpenInModal = displayUrl || dropboxInfo.embeddable;
  const shouldOpenNewTab = dropboxInfo.type === "folder" || dropboxInfo.type === "mov";

  // Derive venue, content type, source for sidebar display
  const venue = parseVenue(asset.destination);
  const contentType = parseContentType(asset.destination);
  const sourceType = parseSourceType(asset.destination);

  return (
    <>
      <div style={styles.sidebarHeader}>
        <span style={styles.sidebarTitle}>Asset Details</span>
        <button
          onClick={onClose}
          style={styles.closeBtn}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f3f4f6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "none";
          }}
        >
          ×
        </button>
      </div>
      
      <div style={styles.sidebarContent}>
        {/* Preview - click to open fullscreen */}
        <div 
          style={{
            ...styles.previewContainer,
            cursor: (displayUrl || previewUrl) ? "pointer" : "default",
            position: "relative",
          }}
          onClick={() => {
            if (shouldOpenNewTab && previewUrl) {
              // Open folders and MOV files in new tab (need Dropbox's player)
              window.open(previewUrl, "_blank");
            } else if (canOpenInModal) {
              onOpenPreview?.();
            } else if (previewUrl) {
              // Fallback: open in new tab
              window.open(previewUrl, "_blank");
            }
          }}
          title={
            shouldOpenNewTab 
              ? (dropboxInfo.type === "folder" ? "Click to open folder in Dropbox" : "Click to open in Dropbox") 
              : (canOpenInModal ? "Click to enlarge" : "")
          }
        >
          {displayUrl ? (
            isVideo && !thumbnailUrl ? (
              <video
                src={displayUrl}
                style={styles.previewImg}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <img
                src={displayUrl}
                alt={asset.title}
                style={styles.previewImg}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )
          ) : (
            <span style={styles.previewPlaceholder}>
              {specs.type === "video" ? "🎬" : "🖼️"}
            </span>
          )}
          {/* Expand icon overlay */}
          {(displayUrl || previewUrl) && (
            <div style={{
              position: "absolute",
              bottom: 8,
              right: 8,
              backgroundColor: "rgba(0,0,0,0.6)",
              borderRadius: 4,
              padding: "4px 8px",
              display: "flex",
              alignItems: "center",
              gap: 4,
              color: "#fff",
              fontSize: 11,
              fontWeight: 500,
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {shouldOpenNewTab ? (
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" />
                ) : (
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                )}
              </svg>
              {shouldOpenNewTab 
                ? (dropboxInfo.type === "folder" ? "Open Folder" : "Open in Dropbox") 
                : "Expand"}
            </div>
          )}
        </div>

        {/* Title & Date */}
        <div style={styles.assetTitle}>{asset.title || "Untitled"}</div>
        <div style={styles.assetDate}>{formatDate(asset.createdTime)}</div>

        {/* Sidebar tags row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {sourceType && SOURCE_CONFIG[sourceType] && (
            <span style={{
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 5,
              color: SOURCE_CONFIG[sourceType].color,
              background: SOURCE_CONFIG[sourceType].bg,
              border: `1px solid ${SOURCE_CONFIG[sourceType].border}`,
            }}>
              {sourceType}
            </span>
          )}
          {contentType && (
            <span style={{
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 5,
              color: "#6b7280",
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
            }}>
              {CONTENT_TYPE_ICONS[contentType] || "📄"} {contentType}
            </span>
          )}
          {venue && (
            <span style={{
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 5,
              color: VENUE_COLORS[venue] || "#6b7280",
              background: VENUE_COLORS[venue] ? `${VENUE_COLORS[venue]}18` : "#f3f4f6",
              border: `1px solid ${VENUE_COLORS[venue] ? `${VENUE_COLORS[venue]}44` : "#e5e7eb"}`,
            }}>
              {venue}
            </span>
          )}
        </div>

        {/* Contact Uploader Button - Lumen8 staff only */}
        {isLumen8Staff && asset.submitterEmail && (
          <a
            href={`mailto:${asset.submitterEmail}?subject=${encodeURIComponent(`Re: ${asset.title || 'Asset'} (${asset.id})`)}&body=${encodeURIComponent(`Hi,\n\nRegarding your uploaded content "${asset.title || 'Asset'}":\n\n`)}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "12px 16px",
              marginBottom: 20,
              backgroundColor: "rgba(47, 214, 181, 0.1)",
              border: "1px solid rgba(47, 214, 181, 0.3)",
              borderRadius: 10,
              color: "#2fd6b5",
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "none",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(47, 214, 181, 0.2)";
              e.currentTarget.style.borderColor = "#2fd6b5";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(47, 214, 181, 0.1)";
              e.currentTarget.style.borderColor = "rgba(47, 214, 181, 0.3)";
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Contact Uploader
          </a>
        )}

        {/* Archive/Restore Button */}
        <button
          onClick={() => asset.archived ? onRestore(asset.id) : onArchive(asset.id)}
          disabled={archiving === asset.id}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "12px 16px",
            marginBottom: 20,
            width: "100%",
            backgroundColor: asset.archived ? "rgba(34, 197, 94, 0.1)" : "rgba(245, 158, 11, 0.1)",
            border: `1px solid ${asset.archived ? "rgba(34, 197, 94, 0.3)" : "rgba(245, 158, 11, 0.3)"}`,
            borderRadius: 10,
            color: asset.archived ? "#22c55e" : "#f59e0b",
            fontSize: 14,
            fontWeight: 600,
            cursor: archiving === asset.id ? "wait" : "pointer",
            fontFamily: "inherit",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            if (archiving !== asset.id) {
              e.currentTarget.style.backgroundColor = asset.archived ? "rgba(34, 197, 94, 0.2)" : "rgba(245, 158, 11, 0.2)";
              e.currentTarget.style.borderColor = asset.archived ? "#22c55e" : "#f59e0b";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = asset.archived ? "rgba(34, 197, 94, 0.1)" : "rgba(245, 158, 11, 0.1)";
            e.currentTarget.style.borderColor = asset.archived ? "rgba(34, 197, 94, 0.3)" : "rgba(245, 158, 11, 0.3)";
          }}
        >
          {archiving === asset.id ? (
            "Processing..."
          ) : asset.archived ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
              </svg>
              Restore Asset
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
              Archive Asset
            </>
          )}
        </button>

        {/* Metadata */}
        <div style={styles.metaSection}>
          <div style={styles.metaGrid}>
            {asset.submitterEmail && (
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Submitter Email</span>
                <a
                  href={`mailto:${asset.submitterEmail}`}
                  style={styles.metaLink}
                >
                  {asset.submitterEmail}
                </a>
              </div>
            )}
            
            {previewUrl && (
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Asset Preview</span>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.metaLink}
                >
                  Open Preview
                </a>
              </div>
            )}
            
            {asset.downloadUrl && (
              <div style={styles.metaItem}>
                <span style={styles.metaLabel}>Asset Download</span>
                <a
                  href={asset.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.metaLink}
                >
                  Download
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Round Allocations - Editable Tags */}
        {(() => {
          const rounds = parseRoundAllocations(asset.roundAllocations);
          const isTvc = (asset.destination || "").toLowerCase().includes("tvc");
          const isLed = (asset.destination || "").toLowerCase().match(/tiled|360/);
          // Show section for any asset that has rounds OR is TVC/LED commercial content
          if (!rounds.length && !isTvc && !isLed) return null;
          
          const assignedRounds = rounds.map(a => ({
            round: (a.round || a.Round || "").toUpperCase(),
            type: a.type || "paid",
          }));
          const roundPool = clubHomeRounds && clubHomeRounds.length > 0 ? clubHomeRounds : AVAILABLE_ROUNDS;
          // For LED: a round is available if it doesn't have BOTH paid and bonus entries
          // For TVC: a round is available if it has no entry at all (no bonus support)
          const availableToAdd = roundPool.filter(r => {
            const hasPaid = assignedRounds.some(a => a.round === r && a.type === "paid");
            const hasBonus = assignedRounds.some(a => a.round === r && a.type === "bonus");
            if (isTvc) return !hasPaid; // TVC: one entry per round
            return !hasPaid || !hasBonus; // LED: available if at least one type is unused
          });
          
          const handleRemoveRound = async (roundToRemove, typeToRemove = "paid") => {
            let found = false;
            const updated = rounds.filter(a => {
              const rnd = (a.round || a.Round || "").toUpperCase();
              const type = a.type || "paid";
              // Remove the first match (in case of duplicates)
              if (!found && rnd === roundToRemove.toUpperCase() && type === typeToRemove) {
                found = true;
                return false;
              }
              return true;
            });
            setRoundSaving(true);
            await onUpdateRounds(asset.id, updated);
            setRoundSaving(false);
          };
          
          const handleAddRound = async (roundToAdd, plays, type = "paid") => {
            const updated = [...rounds, { round: roundToAdd, numberOfRotations: plays || 1, type }];
            // Sort by round order
            updated.sort((a, b) => {
              const ra = (a.round || a.Round || "").toUpperCase();
              const rb = (b.round || b.Round || "").toUpperCase();
              const idxA = roundPool.indexOf(ra);
              const idxB = roundPool.indexOf(rb);
              return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
            });
            setRoundSaving(true);
            setShowRoundPicker(false);
            setPendingRound(null);
            await onUpdateRounds(asset.id, updated);
            setRoundSaving(false);
          };
          
          return (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>Round Allocations</span>
                {roundSaving && <span style={{ fontSize: 10, color: "#2fd6b5", fontWeight: 500 }}>Saving...</span>}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {rounds.map((alloc, i) => {
                  const rnd = (alloc.round || alloc.Round || "?").toUpperCase();
                  const rotations = alloc.numberOfRotations || alloc.rotations || 1;
                  const isBonus = (alloc.type || "paid") === "bonus";
                  return (
                    <span key={i} style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "4px 8px",
                      borderRadius: 6,
                      background: isBonus ? "rgba(167, 139, 250, 0.12)" : "rgba(47, 214, 181, 0.12)",
                      color: isBonus ? "#7c3aed" : "#0d9488",
                      border: `1px solid ${isBonus ? "rgba(167, 139, 250, 0.25)" : "rgba(47, 214, 181, 0.25)"}`,
                    }}>
                      {rnd}
                      <span style={{ fontSize: 10, fontWeight: 400, color: "#6b7280" }}>×{rotations}</span>
                      {isBonus && <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.05em", color: "#7c3aed", opacity: 0.7 }}>BONUS</span>}
                      <button
                        onClick={() => handleRemoveRound(rnd, alloc.type || "paid")}
                        disabled={roundSaving}
                        style={{
                          background: "none",
                          border: "none",
                          padding: "0 0 0 2px",
                          cursor: roundSaving ? "wait" : "pointer",
                          color: "#9ca3af",
                          fontSize: 14,
                          lineHeight: 1,
                          fontWeight: 400,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
                        title={`Remove ${rnd}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
                
                {/* Add Round — two-step: select round, then select plays */}
                {showRoundPicker ? (
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {!pendingRound ? (
                      <select
                        autoFocus
                        value=""
                        onChange={(e) => { if (e.target.value) setPendingRound(e.target.value); }}
                        onBlur={() => { setTimeout(() => { if (!pendingRound) setShowRoundPicker(false); }, 150); }}
                        disabled={roundSaving}
                        style={{
                          fontSize: 12, padding: "4px 8px", borderRadius: 6,
                          border: "1px solid #d1d5db", background: "#fff",
                          color: "#374151", cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        <option value="">Round...</option>
                        {availableToAdd.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    ) : !pendingPlays ? (
                      <>
                        <span style={{
                          fontSize: 12, fontWeight: 600, padding: "4px 8px",
                          borderRadius: 6, background: "rgba(47, 214, 181, 0.12)",
                          color: "#0d9488", border: "1px solid rgba(47, 214, 181, 0.25)",
                        }}>
                          {pendingRound}
                        </span>
                        <select
                          autoFocus
                          value=""
                          onChange={(e) => {
                            if (!e.target.value) return;
                            const plays = parseInt(e.target.value);
                            // TVC content: skip type step, always "paid"
                            if (isTvc) {
                              handleAddRound(pendingRound, plays, "paid");
                              setPendingPlays(null);
                            } else {
                              setPendingPlays(plays);
                            }
                          }}
                          disabled={roundSaving}
                          style={{
                            fontSize: 12, padding: "4px 8px", borderRadius: 6,
                            border: "1px solid #d1d5db", background: "#fff",
                            color: "#374151", cursor: "pointer", fontFamily: "inherit",
                          }}
                        >
                          <option value="">Plays...</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                            <option key={n} value={n}>×{n}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => { setPendingRound(null); setShowRoundPicker(false); }}
                          style={{
                            background: "none", border: "none", cursor: "pointer",
                            fontSize: 12, color: "#9ca3af", padding: "2px 4px",
                          }}
                        >✕</button>
                      </>
                    ) : (
                      <>
                        <span style={{
                          fontSize: 12, fontWeight: 600, padding: "4px 8px",
                          borderRadius: 6, background: "rgba(47, 214, 181, 0.12)",
                          color: "#0d9488", border: "1px solid rgba(47, 214, 181, 0.25)",
                        }}>
                          {pendingRound} ×{pendingPlays}
                        </span>
                        <button
                          onClick={() => { handleAddRound(pendingRound, pendingPlays, "paid"); setPendingPlays(null); }}
                          disabled={roundSaving}
                          style={{
                            fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 5,
                            border: "1px solid rgba(47, 214, 181, 0.4)", cursor: "pointer",
                            background: "rgba(47, 214, 181, 0.15)", color: "#0d9488",
                            fontFamily: "inherit",
                          }}
                        >Paid</button>
                        <button
                          onClick={() => { handleAddRound(pendingRound, pendingPlays, "bonus"); setPendingPlays(null); }}
                          disabled={roundSaving}
                          style={{
                            fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 5,
                            border: "1px solid rgba(167, 139, 250, 0.4)", cursor: "pointer",
                            background: "rgba(167, 139, 250, 0.15)", color: "#7c3aed",
                            fontFamily: "inherit",
                          }}
                        >Bonus</button>
                        <button
                          onClick={async () => {
                            // "Both" adds two entries: one paid, one bonus
                            const updated = [
                              ...parseRoundAllocations(asset.roundAllocations),
                              { round: pendingRound, numberOfRotations: pendingPlays, type: "paid" },
                              { round: pendingRound, numberOfRotations: pendingPlays, type: "bonus" },
                            ];
                            updated.sort((a, b) => {
                              const ra = (a.round || a.Round || "").toUpperCase();
                              const rb = (b.round || b.Round || "").toUpperCase();
                              const idxA = roundPool.indexOf(ra);
                              const idxB = roundPool.indexOf(rb);
                              return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
                            });
                            setPendingPlays(null);
                            setShowRoundPicker(false);
                            setPendingRound(null);
                            setRoundSaving(true);
                            await onUpdateRounds(asset.id, updated);
                            setRoundSaving(false);
                          }}
                          disabled={roundSaving}
                          style={{
                            fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 5,
                            border: "1px solid rgba(245, 158, 11, 0.4)", cursor: "pointer",
                            background: "rgba(245, 158, 11, 0.12)", color: "#d97706",
                            fontFamily: "inherit",
                          }}
                        >Both</button>
                        <button
                          onClick={() => { setPendingPlays(null); setPendingRound(null); setShowRoundPicker(false); }}
                          style={{
                            background: "none", border: "none", cursor: "pointer",
                            fontSize: 12, color: "#9ca3af", padding: "2px 4px",
                          }}
                        >✕</button>
                      </>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowRoundPicker(true)}
                    disabled={roundSaving || !availableToAdd.length}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      fontSize: 11,
                      fontWeight: 500,
                      padding: "4px 8px",
                      borderRadius: 6,
                      background: "none",
                      color: "#6b7280",
                      border: "1px dashed #d1d5db",
                      cursor: roundSaving || !availableToAdd.length ? "default" : "pointer",
                      fontFamily: "inherit",
                    }}
                    onMouseEnter={(e) => { if (availableToAdd.length) { e.currentTarget.style.borderColor = "#2fd6b5"; e.currentTarget.style.color = "#0d9488"; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#6b7280"; }}
                  >
                    + Add Round
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Asset Spec Summary */}
        {asset.specSummary && (
          <div style={styles.specSection}>
            <div style={styles.specTitle}>Asset Spec Summary</div>
            <SpecSummaryTable summary={asset.specSummary} />
          </div>
        )}

        {/* Notes */}
        {asset.notes && (
          <div style={{ ...styles.metaSection, marginTop: 16 }}>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Notes</span>
              <span style={styles.metaValue}>{asset.notes}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================
// PREVIEW MODAL (Fullscreen Overlay)
// ============================================================
function PreviewModal({ asset, onClose }) {
  const [closeHovered, setCloseHovered] = useState(false);
  const thumbnailUrl = getThumbnailUrl(asset);
  const previewUrl = getPreviewUrl(asset);
  const specs = parseAssetSpecs(asset);
  const isVideo = specs.type === "video";
  
  // Analyze Dropbox URL
  const dropboxInfo = analyzeDropboxUrl(previewUrl);
  
  // Determine what URL to use for display
  // Priority: embeddable Dropbox URL > thumbnail > non-Dropbox preview URL
  const displayUrl = dropboxInfo.embeddable 
    ? dropboxInfo.embedUrl 
    : (thumbnailUrl || (!isDropboxUrl(previewUrl) ? previewUrl : null));
  
  const displayIsVideo = dropboxInfo.type === "video" || (isVideo && !thumbnailUrl);

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div 
      style={styles.modalOverlay}
      onClick={(e) => {
        // Close when clicking the overlay background
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div style={styles.modalContent}>
        {/* Close button */}
        <button
          onClick={onClose}
          onMouseEnter={() => setCloseHovered(true)}
          onMouseLeave={() => setCloseHovered(false)}
          style={{
            ...styles.modalClose,
            ...(closeHovered ? styles.modalCloseHover : {}),
          }}
        >
          ×
        </button>

        {/* Media content */}
        {displayUrl ? (
          displayIsVideo ? (
            <video
              src={displayUrl}
              controls
              autoPlay
              style={styles.modalMedia}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <img
              src={displayUrl}
              alt={asset.title}
              style={styles.modalMedia}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )
        ) : (
          <div style={styles.modalPlaceholder}>
            <span style={styles.modalPlaceholderIcon}>
              {isVideo ? "🎬" : "🖼️"}
            </span>
            <span style={styles.modalPlaceholderText}>
              {dropboxInfo.type === "folder" 
                ? "Folder preview not available" 
                : dropboxInfo.type === "mov"
                  ? "MOV files must be opened in Dropbox"
                  : "No preview available"}
            </span>
          </div>
        )}

        {/* Title */}
        <div style={styles.modalTitle}>{asset.title || "Untitled"}</div>

        {/* Action buttons */}
        <div style={styles.modalActions}>
          {previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.modalActionBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(47, 214, 181, 0.3)";
                e.currentTarget.style.borderColor = "#2fd6b5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
              </svg>
              Open in New Tab
            </a>
          )}
          {asset.downloadUrl && (
            <a
              href={asset.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.modalActionBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(47, 214, 181, 0.3)";
                e.currentTarget.style.borderColor = "#2fd6b5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
              </svg>
              Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default IngestFlowGalleryPage;
